import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Multiselect } from "react-widgets";

import { xml2js } from "../../json/xml2js";
import {
  deleteFormSubmission,
  getConfig,
  getSubmission,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
  saveForm,
  sendShareEmail,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldArray,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import FieldTable from "../common/FieldTable";
import FileUpload from "../common/FileUpload";
import FormButtonBar from "../common/FormButtonBar";
import FormInput from "../common/FormInput";
import ShareModal from "../common/ShareModal";
import SharedComment from "../common/SharedComment";
import { getAttachments, sendEmail } from "../services/soar";

function SAFYFPPortalCMS() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and attachments
  const [data, setData] = useState({});
  const [fileAttachments, setFileAttachments] = useState([]);
  //Touched fields
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);
  const isMountedRef = useRef(true);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [submissionid]);

  useEffect(() => {
    if (!formid || loadStatus.loadingConfig !== undefined || !safyInfo?.user) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: "",
          ContactName: "",
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, dataSet: true };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false };
        });
      }
    };

    loadConfig();
  }, [loadStatus.loadingConfig, formid, safyInfo?.user, submissionid]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      console.log(
        "helperConfiguration JSON:",
        formsyConfig.HelperConfiguration[0]
      );
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow, unshift = false) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow, unshift);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(data, formsyDetails, null, null, navigate, location);
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (
    comment,
    commentToEdit,
    selectedUsers,
    commentText
  ) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);

      if (selectedUsers.length > 0) {
        sendEmail(
          selectedUsers.map((user) => user.email),
          "",
          "Developer Change Request Comment",
          `<p>Link: ${window.location.href}<br/>User: ${safyInfo.user.displayName}, ${safyInfo.user.domainName}</p>
          <p>Comment: ${commentText}</p>`
        );
      }
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  const loadFiles = useCallback(async () => {
    if (submissionid) {
      const response = await getAttachments(submissionid);
      if (response?.data.length > 0) {
        setFileAttachments(response.data);
      }
    }
  }, [submissionid]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const availableFiles = useMemo(() => {
    return fileAttachments.map((file) => ({
      _id: file.FileId,
      name: file.FileName,
    }));
  }, [fileAttachments]);

  ////UI
  return loadStatus.dataSet ? (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onDelete={handleDelete}
          onComment={toggleCommentModal}
          onShare={toggleShareModal}
        />
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end m-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <FieldTable name="Config" commentsHandler={handleComments}>
          <div className="row">
            <div className="col py-2 h6">Category Name</div>
            <div className="col py-2 h6">Files</div>
          </div>
          {getFieldArray(data.Config).map((item, index) => (
            <React.Fragment key={`Config.${item._id || index}`}>
              <div className="row border-2 border-bottom border-primary mb-2 pb-2">
                <div className="col d-flex">
                  <i
                    className="far fa-trash-alt me-2 d-print-none"
                    onClick={() => {
                      handleDeleteRow(index, "Config");
                    }}
                  />
                  <div className="flex-fill">
                    <FormInput
                      name="Name"
                      value={item.Name}
                      onChange={(e) => {
                        handleRowChange(e, index, "Config");
                      }}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="col">
                  <Multiselect
                    data={availableFiles}
                    dataKey="_id"
                    textField="name"
                    value={item.Files}
                    onChange={(items) => {
                      const e = {
                        currentTarget: {
                          name: "Files",
                          type: "multi",
                          value: items.map((item) => item._id),
                        },
                      };
                      handleRowChange(e, index, "Config");
                    }}
                  />
                </div>
              </div>
            </React.Fragment>
          ))}
          {!formDisabled ? (
            <button
              className="btn btn-primary mb-2"
              onClick={() => {
                handleAddRow("Config", { IsEditable: true });
              }}
            >
              Add
            </button>
          ) : null}
        </FieldTable>
        <div className="mt-4">
          <FileUpload
            header="Foster Parent Portal Help Resources"
            objectId={submissionid}
            objectType="FP Portal Help"
            savedFiles={fileAttachments}
            printImg={true}
            loadFiles={loadFiles}
          />
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <SharedComment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  ) : null;
}

export default SAFYFPPortalCMS;
